import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import { useDocumentSearch } from '@terminal/views/alfred/document-search/use-document-search/useDocumentSearch';
import { useChat as useQuickAnalysisChat } from '@terminal/views/alfred/quick-analysis/use-chat/useChat';
import { getInventoryItemCategories } from '@terminal/views/alfred/research/InventoryContent/utils/getInventoryItemCategories';
import { getConversationInventory } from '@terminal/views/alfred/research/services/inventory-service';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useEffect, useRef } from 'react';

import { getNewlyCompletedResponses } from './utils/getNewlyCompletedResponses';
import {
  getNewlyCompletedStreamingResponses,
  type StreamingMessage,
} from './utils/getNewlyCompletedStreamingResponses';

interface StreamingChatState {
  sessionId: string;
  messages: StreamingMessage[];
}

export const useAlfredResponseTracking = () => {
  const token = useAuthStore(s => s.token);
  const { trackAlfredResponseReceived } = usePosthogEvents();
  const reportedResponseIdsRef = useRef<Set<string>>(new Set());

  const reportResponseReceived = async (
    conversationId: string,
    requestId: string,
    type?: string
  ) => {
    if (reportedResponseIdsRef.current.has(requestId)) {
      return;
    }

    reportedResponseIdsRef.current.add(requestId);

    const inventory = await getConversationInventory(conversationId);

    trackAlfredResponseReceived({
      source: POSTHOG_TRACKING_SOURCES.ALFRED,
      type,
      conversationId,
      messageId: requestId,
      inventoryItemCategories: getInventoryItemCategories(inventory),
    });
  };

  // Quick Analysis and Document Search stream over HTTP and have no inventory,
  // so they report an empty category list.
  const reportStreamingResponseReceived = (
    sessionId: string,
    messageId: string,
    type: string
  ) => {
    if (reportedResponseIdsRef.current.has(messageId)) {
      return;
    }

    reportedResponseIdsRef.current.add(messageId);

    trackAlfredResponseReceived({
      source: POSTHOG_TRACKING_SOURCES.ALFRED,
      type,
      conversationId: sessionId,
      messageId,
      inventoryItemCategories: [],
    });
  };

  const handleStreamingStoreChange =
    (type: string) =>
    (state: StreamingChatState, previousState: StreamingChatState) => {
      getNewlyCompletedStreamingResponses(
        state.messages,
        previousState.messages
      ).forEach(messageId => {
        reportStreamingResponseReceived(state.sessionId, messageId, type);
      });
    };

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const unsubscribeResearch = useChat.subscribe((state, previousState) => {
      const newlyCompleted = getNewlyCompletedResponses(
        state.conversations,
        previousState.conversations
      );

      newlyCompleted.forEach(({ conversationId, requestId, type }) => {
        reportResponseReceived(conversationId, requestId, type);
      });
    });

    const unsubscribeQuickAnalysis = useQuickAnalysisChat.subscribe(
      handleStreamingStoreChange('Quick')
    );
    const unsubscribeDocumentSearch = useDocumentSearch.subscribe(
      handleStreamingStoreChange('Document')
    );

    return () => {
      unsubscribeResearch();
      unsubscribeQuickAnalysis();
      unsubscribeDocumentSearch();
    };
  }, [token]);
};
